import { useCallback } from 'react';
import { Bell, Menu, Search } from 'lucide-react';
import { useTranslation, i18next } from '@/lib/i18n';
import { Button, LocaleSwitcher } from '@/components/ui';
import { useAuthContext } from '@/providers/AuthProvider';
import { FavoriteMenuBar } from './FavoriteMenus';

interface TopbarProps {
  /** 200(b) — 모바일(<md) 햄버거 클릭 시 사이드바 드로어 열기. 데스크톱 무관. */
  readonly onMenuClick?: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const LOCALE_OPTIONS = [
  { code: 'ko-KR', label: '한국어' },
  { code: 'en-US', label: 'English' },
  { code: 'ja-JP', label: '日本語' },
  { code: 'zh-CN', label: '中文(简体)' },
] as const;

export const Topbar = ({ onMenuClick }: TopbarProps = {}) => {
  const { t } = useTranslation('common');
  const { currentAdmin } = useAuthContext();
  const currentLang = i18next.language ?? 'ko-KR';

  const initials = currentAdmin?.name
    ? currentAdmin.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const openCommandPalette = useCallback(() => {
    window.dispatchEvent(new CustomEvent('open-command-palette'));
  }, []);

  const handleLangChange = useCallback((locale: string) => {
    void i18next.changeLanguage(locale);
  }, []);

  return (
    <header
      className="sticky top-0 z-10 flex h-[56px] items-center gap-2 px-5 lg:px-6"
      style={{
        background: 'var(--surface-0)',
        borderBottom: '1px solid var(--divider-strong)',
      }}
    >
      {/* 200(b) — 모바일(<md) 햄버거: 사이드바 드로어 열기. md+ 에서는 숨김(사이드바 상시 노출). */}
      <Button
        variant="ghost"
        size="sm"
        className="h-9 rounded-md px-2 md:hidden"
        aria-label={t('topbar.openMenu', '메뉴 열기') as string}
        onClick={onMenuClick}
        data-testid="topbar-menu-trigger"
      >
        <Menu size={18} style={{ color: 'var(--content-default)' }} />
      </Button>

      {/* Global search → opens command palette */}
      <button
        type="button"
        onClick={openCommandPalette}
        className="group flex h-9 w-full max-w-[360px] shrink-0 items-center gap-2 rounded-md px-3 text-[13px] outline-none transition-colors hover:border-[color:var(--content-soft)] focus:[box-shadow:var(--focus-ring-brand)]"
        style={{
          background: 'var(--surface-1)',
          border: '1px solid var(--divider-strong)',
          color: 'var(--content-muted)',
        }}
        aria-label={t('topbar.search', '검색') as string}
        data-testid="topbar-command-trigger"
      >
        <Search size={14} aria-hidden="true" />
        <span className="flex-1 text-left">
          {t('topbar.searchPlaceholder', '페이지·작업 검색…')}
        </span>
        <kbd
          className="font-mono inline-flex h-5 items-center gap-0.5 rounded px-1.5 text-[10px] font-medium"
          style={{
            background: 'var(--surface-raised)',
            color: 'var(--content-default)',
            border: '1px solid var(--divider-strong)',
          }}
        >
          {isMac ? '⌘' : 'Ctrl'} K
        </kbd>
      </button>

      {/* QA-509 즐겨찾는 메뉴(계정별 최대 7개). */}
      <FavoriteMenuBar />

      <div className="ml-auto flex items-center gap-2">
        {/* Language switcher */}
        <LocaleSwitcher
          supportedLocales={LOCALE_OPTIONS}
          currentLocale={currentLang}
          onChange={handleLangChange}
          data-testid="topbar-language-switcher"
        />

        {/* Notification */}
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 rounded-md px-2"
          aria-label={t('topbar.notifications', '알림')}
        >
          <Bell size={16} style={{ color: 'var(--content-muted)' }} />
        </Button>

        <span className="mx-1 h-6 w-px" style={{ background: 'var(--divider-strong)' }} aria-hidden="true" />

        {/* User identity */}
        <div className="flex items-center gap-2">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold text-white select-none"
            style={{ background: 'var(--accent-default)' }}
            aria-label={currentAdmin?.name ?? ''}
          >
            {initials}
          </div>
          {currentAdmin ? (
            <div className="hidden flex-col leading-tight md:flex">
              <span className="text-[12px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                {currentAdmin.name}
              </span>
              <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {currentAdmin.roles[0] ?? 'Admin'}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
};
